/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { Font, GetTextDimensionsType, TextDimensions } from '#hooks/PRIVATE_useTextDimensions';
import { getTextInfo } from '#PRIVATE_VisSVGText';
import { Dimension } from '#utils/UNSAFE_visTypes/common';
import { PieSliceInfo } from './pieChart.types';
import { degreesToRads } from './utils';

type TextInfo = {
  lines: string[];
  dims: TextDimensions[];
  didNotFit: boolean;
  height: number;
  width: number;
  isMultiline: boolean;
  lineHeight: number;
  isTruncated: boolean;
};

export type PieLabelInfo<K> = {
  position: number;
  angle: number;
  slice: PieSliceInfo<K>;
  width: number;
  height: number;
  x?: number;
  y?: number;
  minY?: number;
  maxY?: number;
  startPt?: {
    x: number;
    y: number;
  };
  endPt?: {
    x: number;
    y: number;
  };
  midPt?: {
    x: number;
    y: number;
  };
  textInfo?: TextInfo;
};

const OUTSIDE_LABEL_DISTANCE = 1.04;
const _COLLISION_MARGIN = 1;
const _FEELER_RAD_MINSIZE = 0.1;
const _FEELER_HORIZ_MINSIZE = 0.1;
const _LABEL_TO_FEELER_OFFSET = 0.5;
const _ALL_COLLISION = 2;
const _HALF_COLLISION = 1;
const _NO_COLLISION = 0;
const _LABEL_TO_FEELER_DISTANCE = 3;

function degreesToRadians(degrees: number) {
  return degrees * (Math.PI / 180);
}

function reflectAngleOverYAxis(angle: number, cx: number, cy: number, rx: number, ry: number) {
  const radian = degreesToRadians(360 - angle);
  const cosine = Math.cos(radian);
  const sine = Math.sin(radian);

  return { x: cx + cosine * rx, y: cy + sine * ry };
}

function skipSliceLabel<K>(slice: PieSliceInfo<K>, seriesCount: number) {
  const { angleExtent } = slice;
  return angleExtent < 3 && seriesCount > 120;
}

/**
 * Returns true if this slice contains the given coordinates.
 * @param {number} x
 * @param {number} y
 * @return {boolean}
 */
export function sliceContainsPoint<K>(
  slice: PieSliceInfo<K>,
  x: number,
  y: number,
  cx: number,
  cy: number,
  innerRadius: number,
  radius: number
) {
  const { angleExtent, startAngle } = slice;
  const ir = innerRadius;
  const c = { x: cx, y: cy };
  const cos = (x - c.x) / radius;
  const sin = (y - c.y) / radius;

  // Compute the angle
  let angle = -Math.atan2(sin, cos) * (180 / Math.PI); // in degrees
  // First adjust angle to be greater than the start angle.
  while (angle < startAngle) angle += 360;
  // Then adjust to be within 360 degrees of it
  while (angle - startAngle >= 360) angle -= 360;

  const distance = Math.pow(cos, 2) + Math.pow(sin, 2);
  const containsRadius = Math.sqrt(distance) > ir / radius && distance <= 1;
  const containsAngle = angle <= startAngle + angleExtent;
  return containsRadius && containsAngle;
}

export function getInsideLabelsInfo<K>(
  slices: PieSliceInfo<K>[],
  innerRadius: number,
  radius: number,
  pieCenter: { cx: number; cy: number },
  defaultFontSize: string,
  getTextDimensions: GetTextDimensionsType
): [PieLabelInfo<K>[], Set<number>] {
  const insideLabels: PieLabelInfo<K>[] = [];

  const outsideLabelsIndex = new Set<number>();

  const { cx, cy } = pieCenter;

  for (const slice of slices) {
    const { labelPosition: l, startAngle, angleExtent, label, labelStyle } = slice;
    const labelPosition = l || 'auto';

    if (labelPosition === 'none') {
      continue;
    }

    if (labelPosition === 'outside' || skipSliceLabel(slice, slices.length) || label == null) {
      outsideLabelsIndex.add(slice.index);
      continue;
    }

    const midAngle = startAngle + angleExtent / 2;
    const ir = innerRadius;
    const center = pieCenter;
    let posX, posY;

    if (slices.length === 1) {
      posX = center.cx;
      posY = center.cy;
    } else {
      const offset = Math.max(0.45, 0.65 - (0.45 * ir) / Math.max(radius, 0.001));
      const midPt = reflectAngleOverYAxis(
        midAngle,
        center.cx,
        center.cy,
        ir + (radius - ir) * offset,
        ir + (radius - ir) * offset
      );

      posX = midPt.x;
      posY = midPt.y;
    }

    const sliceLabelPosition = {
      x: posX,
      y: posY
    };

    const sliceLabelDims = getTextDimensions(label, { fontSize: defaultFontSize, ...labelStyle });

    let x1 = posX;
    let x2 = posX;
    const y1 = posY - sliceLabelDims.height / 2;
    const y2 = posY + sliceLabelDims.height / 2;

    while (
      sliceContainsPoint(slice, x1, y1, cx, cy, innerRadius, radius) &&
      sliceContainsPoint(slice, x1, y2, cx, cy, innerRadius, radius)
    ) {
      x1--;
    }

    while (
      sliceContainsPoint(slice, x2, y1, cx, cy, innerRadius, radius) &&
      sliceContainsPoint(slice, x2, y2, cx, cy, innerRadius, radius)
    ) {
      x2++;
    }

    x1 = Math.ceil(x1 + 3);
    x2 = Math.floor(x2 - 3);

    let usableSpace = 2 * Math.min(posX - x1, x2 - posX);
    if (usableSpace < sliceLabelDims.width) {
      sliceLabelPosition.x = (x1 + x2) / 2;
      usableSpace = x2 - x1;
    }

    if (labelPosition === 'inside') {
      const textWidth = sliceLabelDims.width;
      if (textWidth < usableSpace) {
        insideLabels.push({
          position: midAngle,
          angle: midAngle,
          slice,
          textInfo: getTextInfo(
            label,
            { fontSize: defaultFontSize, ...labelStyle },
            usableSpace,
            sliceLabelDims.height,
            false,
            label.length,
            getTextDimensions
          ),
          ...sliceLabelDims,
          ...sliceLabelPosition
        });
      }
    } else {
      const minChars = label.length;
      const textInfo = getTextInfo(
        label,
        { fontSize: defaultFontSize, ...labelStyle },
        usableSpace,
        sliceLabelDims.height,
        false,
        minChars,
        getTextDimensions
      );

      if (!textInfo.didNotFit) {
        insideLabels.push({
          position: midAngle,
          angle: midAngle,
          slice,
          textInfo,
          ...sliceLabelDims,
          ...sliceLabelPosition
        });
      } else {
        outsideLabelsIndex.add(slice.index); // Indicates outside label is needed
      }
    }
  }
  return [insideLabels, outsideLabelsIndex];
}

export const layoutPieChartLabels = <K>(
  slices: PieSliceInfo<K>[],
  pieCenter: { cx: number; cy: number },
  innerRadius: number,
  radius: number,
  availSpace: Dimension,
  getTextDimensions: GetTextDimensionsType,
  defaultFontSize: string
) => {
  const [insideLabels, outsideLabels] = getInsideLabelsInfo(
    slices,
    innerRadius,
    radius,
    pieCenter,
    defaultFontSize,
    getTextDimensions
  );

  const { leftLabels, rightLabels } = generateInitialLayout(
    slices,
    outsideLabels,
    availSpace,
    pieCenter,
    radius,
    defaultFontSize,
    getTextDimensions
  );

  const leftColl = _refineInitialLayout(slices, radius, pieCenter, availSpace, leftLabels, 'left');
  const rightColl = _refineInitialLayout(
    slices,
    radius,
    pieCenter,
    availSpace,
    rightLabels,
    'right'
  );

  if (leftColl === _HALF_COLLISION && rightColl !== _NO_COLLISION) {
    _columnLabels(slices, availSpace, pieCenter, radius, leftLabels, true, true, true);
  }

  if (leftColl !== _NO_COLLISION && rightColl === _HALF_COLLISION) {
    _columnLabels(slices, availSpace, pieCenter, radius, rightLabels, false, true, true);
  }

  setLabelsAndFeelers(
    leftLabels,
    availSpace,
    pieCenter,
    radius,
    'left',
    { fontSize: defaultFontSize },
    getTextDimensions
  );

  setLabelsAndFeelers(
    rightLabels,
    availSpace,
    pieCenter,
    radius,
    'right',
    { fontSize: defaultFontSize },
    getTextDimensions
  );

  return { insideLabels, rightLabels, leftLabels };
};

export function generateInitialLayout<K>(
  slices: PieSliceInfo<K>[],
  outsideLabels: Set<number>,
  availSpace: Dimension,
  center: { cx: number; cy: number },
  radius: number,
  defaultFontSize: string,
  getTextDimensions: GetTextDimensionsType
) {
  const leftLabels: PieLabelInfo<K>[] = [];
  const rightLabels: PieLabelInfo<K>[] = [];
  const { cx, cy } = center;

  for (let i = 0; i < slices.length; i++) {
    const slice = slices[i];
    const { labelPosition, startAngle, angleExtent, label, labelStyle } = slice;

    if (
      label == null ||
      labelPosition == 'none' ||
      labelPosition == 'inside' ||
      skipSliceLabel(slices[i], slices.length) ||
      !outsideLabels.has(slice.index)
    ) {
      continue;
    }

    let middleAngle = startAngle + angleExtent / 2;
    if (middleAngle > 360) middleAngle -= 360;
    if (middleAngle < 0) middleAngle += 360;

    const labelPt = reflectAngleOverYAxis(
      middleAngle,
      cx,
      cy,
      radius * OUTSIDE_LABEL_DISTANCE,
      radius * OUTSIDE_LABEL_DISTANCE
    );

    const isLeftSide = middleAngle >= 90 && middleAngle < 270;
    const maxLabelWidth = getMaxLabelWidth(availSpace, labelPt.x, isLeftSide);

    const textInfo = getTextInfo(
      label,
      { fontSize: defaultFontSize, ...labelStyle },
      maxLabelWidth,
      Infinity,
      true,
      1,
      getTextDimensions
    );
    const tmDimPt = { x: textInfo.width, y: textInfo.height };

    const midArea = 15;

    if (middleAngle < 180 - midArea && middleAngle > midArea) {
      //upper half
      labelPt.y -= tmDimPt.y * 1;
    } else if (middleAngle < midArea || middleAngle > 360 - midArea) {
      //right side, near horizontal
      labelPt.y -= tmDimPt.y * 0.5;
      labelPt.x += tmDimPt.y * 0.2;
    } else if (middleAngle > 180 - midArea && middleAngle < 180 + midArea) {
      //left side, near horizontal
      labelPt.y -= tmDimPt.y * 0.5;
      labelPt.x -= tmDimPt.y * 0.2;
    }

    if (slices.length == 1)
      // only 1 label
      labelPt.x -= tmDimPt.x / 2; //position the label at the center

    if (labelPt.y < availSpace.y || labelPt.y + tmDimPt.y > availSpace.y + availSpace.height)
      // label will not fit with appropriate spacing
      continue;

    let pa;
    if (middleAngle >= 90.0 && middleAngle < 270.0) {
      pa = middleAngle - 90.0;
      createLabelInfo(slice, middleAngle, pa, tmDimPt, labelPt, textInfo, leftLabels);
    } else {
      // right side
      // normalize from 0 to 180
      pa = middleAngle <= 90.0 ? Math.abs(90 - middleAngle) : 180 - (middleAngle - 270);
      createLabelInfo(slice, middleAngle, pa, tmDimPt, labelPt, textInfo, rightLabels);
    }
  }

  return { leftLabels, rightLabels };
}

export function getMaxLabelWidth(availSpace: Dimension, labelX: number, isLeftSide: boolean) {
  return isLeftSide ? labelX - availSpace.x : availSpace.x + availSpace.width - labelX;
}

export function createLabelInfo<K>(
  slice: PieSliceInfo<K>,
  midAngle: number,
  position: number,
  tmDimPt: { x: number; y: number },
  labelPt: { x: number; y: number },
  textInfo: TextInfo,
  labelInfoArray: PieLabelInfo<K>[]
) {
  let insertPos = -1;

  // insertion "sort"
  for (let j = 0; j < labelInfoArray.length; j++) {
    const labelInfo = labelInfoArray[j];
    if (labelInfo['position'] && labelInfo['position'] > position) {
      insertPos = j;
      break;
    }
  }

  if (insertPos == -1) insertPos = labelInfoArray.length;

  const labelInfo = {
    position,
    angle: midAngle,
    width: tmDimPt.x,
    height: tmDimPt.y,
    x: labelPt.x,
    y: labelPt.y,
    slice,
    textInfo
  };

  labelInfoArray.splice(insertPos, 0, labelInfo);
}

export function _refineInitialLayout<K>(
  slices: PieSliceInfo<K>[],
  radius: number,
  center: { cx: number; cy: number },
  availableSpace: Dimension,
  labelInfoArray: PieLabelInfo<K>[],
  side: 'left' | 'right'
) {
  if (labelInfoArray.length < 1) {
    return;
  }

  let lastY = availableSpace.y; //think again!!
  let collisionTop = false;
  let collisionCentral = false;
  let collisionBottom = false;
  let labelBottom = 0;

  let labelInfo;

  let bottomQuarter = false;
  let prevBottomQuarter;
  let collide = false;
  const isLeftSideLabels = side == 'left';

  for (let i = 0; i < labelInfoArray.length; i++) {
    labelInfo = labelInfoArray[i];

    prevBottomQuarter = bottomQuarter;
    if (labelInfo.position! > 90) bottomQuarter = true;

    labelBottom = labelInfo.y! + labelInfo.height!;

    collide = lastY - labelInfo.y! > _COLLISION_MARGIN;

    if (collide) {
      if (!bottomQuarter) {
        collisionTop = true;
      } else if (!prevBottomQuarter) {
        collisionCentral = true;
      } else {
        collisionBottom = true;
      }
    }

    if (labelBottom > lastY) {
      lastY = labelBottom;
    }
  }

  if ((collisionTop && collisionBottom) || collisionCentral) {
    _columnLabels(
      slices,
      availableSpace,
      center,
      radius,
      labelInfoArray,
      isLeftSideLabels,
      true,
      true
    );
    return _ALL_COLLISION;
  } else if (collisionTop) {
    _columnLabels(
      slices,
      availableSpace,
      center,
      radius,
      labelInfoArray,
      isLeftSideLabels,
      true,
      false
    );
    return _HALF_COLLISION;
  } else if (collisionBottom) {
    _columnLabels(
      slices,
      availableSpace,
      center,
      radius,
      labelInfoArray,
      isLeftSideLabels,
      false,
      true
    );
    return _HALF_COLLISION;
  }
  return _NO_COLLISION;
}

export function _getRadFeelerAngle<K>(
  labelInfo: PieLabelInfo<K>,
  x: number,
  y: number,
  radius: number,
  center: { cx: number; cy: number }
) {
  const ma = labelInfo.angle;
  const startPt = reflectAngleOverYAxis(ma, center.cx, center.cy, radius, radius);
  return Math.atan2(Math.abs(x - startPt.x), startPt.y - y);
}

export function getMiddleLabel<K>(alabels: PieLabelInfo<K>[]) {
  let bestAngle = 91;
  let bestIndex = -1;
  for (let i = 0; i < alabels.length; i++) {
    const pa = alabels[i]['position']!;
    if (Math.abs(pa - 90) < bestAngle) {
      bestAngle = Math.abs(pa - 90);
      bestIndex = i;
    }
  }
  return bestIndex;
}

export function setOptimalLabelPos<K>(
  radius: number,
  labelInfo: PieLabelInfo<K>,
  vertX: number,
  center: { cx: number; cy: number }
) {
  //set optimal X
  labelInfo['x'] = vertX;

  const b = radius * (1 + _FEELER_RAD_MINSIZE);
  const angleInRad = degreesToRads(labelInfo['position']);
  const heightFromCenter = b * Math.cos(angleInRad);
  const tilt = 0;
  let optimalY = center.cy - heightFromCenter - labelInfo.height * _LABEL_TO_FEELER_OFFSET + tilt;
  const _minY = labelInfo['minY']!;
  const _maxY = labelInfo['maxY']!;
  if (_minY <= _maxY) {
    optimalY = Math.max(optimalY, _minY);
    optimalY = Math.min(optimalY, _maxY);
  }
  labelInfo['y'] = optimalY;
}

export function _columnLabels<K>(
  slices: PieSliceInfo<K>[],
  availableSpace: Dimension,
  center: { cx: number; cy: number },
  radius: number,
  alabels: PieLabelInfo<K>[],
  isLeft: boolean,
  isTop: boolean,
  isBottom: boolean
) {
  const frame = availableSpace;
  let minY = frame.y;
  let maxY = frame.y + frame.height;
  let i;
  let labelInfo;
  let pa = 0;
  let radFeelerAngle;

  //determine the position where the column will be aligned
  let vertX = center.cx;
  let feelerX;
  const minFeelerDist = radius * (1 + _FEELER_RAD_MINSIZE + _FEELER_HORIZ_MINSIZE);

  if (isLeft) {
    vertX -= minFeelerDist;
    feelerX = vertX + radius * _FEELER_HORIZ_MINSIZE;
  } else {
    vertX += minFeelerDist;
    feelerX = vertX - radius * _FEELER_HORIZ_MINSIZE;
  }

  //set the minimum heights that ensures as many labels as possible are displayed
  for (i = 0; i < alabels.length; i++) {
    labelInfo = alabels[i];
    pa = degreesToRads(labelInfo.position);
    radFeelerAngle = _getRadFeelerAngle(labelInfo, feelerX, minY, radius, center);

    // Remove labels that are more than a certain angle away from the slice.
    if (radFeelerAngle - pa > 0.45 * Math.PI || skipSliceLabel(alabels[i].slice, slices.length)) {
      alabels.splice(i, 1);
      i--;
    } else {
      alabels[i].minY = minY;
      minY += alabels[i].height;
    }
  }

  //set the maximum heights that ensures as many labels as possible are displayed
  for (i = alabels.length - 1; i >= 0; i--) {
    labelInfo = alabels[i];
    const position = labelInfo['position'];
    pa = degreesToRads(position);
    radFeelerAngle = _getRadFeelerAngle(labelInfo, feelerX, maxY, radius, center);

    // Remove labels that are more than a certain angle away from the slice.
    if (pa - radFeelerAngle > 0.45 * Math.PI || skipSliceLabel(alabels[i].slice, slices.length)) {
      alabels.splice(i, 1);
    } else {
      maxY -= alabels[i].height;
      alabels[i].maxY = maxY;
    }
  }

  if (alabels.length == 0) return;

  const startIndex = getMiddleLabel(alabels);
  const startLabel = alabels[startIndex];

  //if the column is only partial but there are too many labels, then set the whole side as column
  if (isTop && !isBottom) {
    if (startLabel.minY! + startLabel.height > center.cy) {
      isBottom = true;
    }
  }
  if (isBottom && !isTop) {
    if (startLabel.maxY! < center.cy) {
      isTop = true;
    }
  }

  const labelPostion = startLabel['position'];
  if ((isBottom && isTop) || (labelPostion > 90 && isBottom) || (labelPostion <= 90 && isTop)) {
    setOptimalLabelPos(radius, startLabel, vertX, center);
    startLabel.slice.hasFeelers = true;
  }

  let highestY = startLabel.y!;
  let lowestY = startLabel.y! + startLabel.height;

  let optimalY;
  let labelHeight;

  if (isTop) {
    //labels above the start label
    for (i = startIndex - 1; i >= 0; i--) {
      labelInfo = alabels[i];
      labelHeight = labelInfo.height;
      setOptimalLabelPos(radius, labelInfo, vertX, center);
      labelInfo.slice.hasFeelers = true;

      //avoid collision with the label below
      optimalY = labelInfo.y!;
      if (optimalY + labelHeight < highestY) {
        highestY = optimalY;
      } else {
        highestY -= labelHeight;
      }
      labelInfo.y = highestY;
    }
  }

  if (isBottom) {
    //labels below the start label
    for (i = startIndex + 1; i < alabels.length; i++) {
      labelInfo = alabels[i];
      labelHeight = labelInfo.height;
      setOptimalLabelPos(radius, labelInfo, vertX, center);
      labelInfo.slice.hasFeelers = true;

      //avoid collision with the label above
      optimalY = labelInfo.y!;
      if (optimalY > lowestY) {
        lowestY = optimalY + labelHeight;
      } else {
        lowestY += labelHeight;
      }
      labelInfo.y = lowestY - labelHeight;
    }
  }
}

export function setLabelsAndFeelers<K>(
  alabels: PieLabelInfo<K>[],
  availSpace: Dimension,
  center: { cx: number; cy: number },
  radius: number,
  side: 'left' | 'right',
  labelStyle: Font,
  getTextDimension: GetTextDimensionsType
) {
  if (alabels == null || alabels.length <= 0) return;

  let i;
  let slice;
  let sliceLabel;
  const isLeftSide = side === 'left';
  const frame = availSpace;

  let excessWidth = Infinity;
  let excessLength;

  // Determine how much the horizontal feelers can be shortened
  for (i = 0; i < alabels.length; i++) {
    const labelInfo = alabels[i];

    const style = { ...labelStyle, ...labelInfo.slice.labelStyle };
    if (labelInfo.slice['hasFeelers']) {
      excessLength = _calculateFeeler(labelInfo, center, radius, isLeftSide);

      const fontHeight = getTextDimension('MW', style).height;
      const labelWidth = labelInfo['width'];
      const minLabelWidth = Math.min(2 * fontHeight, labelWidth);

      const maxLabelWidth = getMaxLabelWidth(availSpace, labelInfo['x']!, isLeftSide);

      // Remove feelers for labels that will not be rendered and ignore for excess width calculation
      if (maxLabelWidth + excessLength < minLabelWidth || labelInfo['width'] == 0) {
        labelInfo.textInfo = undefined;
        labelInfo.slice.hasFeelers = false;
        continue;
      }
      excessWidth = Math.min(excessWidth, excessLength);
    } else {
      labelInfo.slice.hasFeelers = false;
    }
  }

  for (i = 0; i < alabels.length; i++) {
    const labelInfo = alabels[i];
    slice = labelInfo.slice;
    sliceLabel = labelInfo.slice.label;
    if (!sliceLabel) continue;

    if (labelInfo.slice.hasFeelers) {
      // shorten the horizontal feelers
      if (isLeftSide) {
        labelInfo.x = labelInfo.x! + excessWidth;
      } else {
        labelInfo.x = labelInfo.x! - excessWidth;
      }
      // setup the feeler line (let it clip if needed)
      _calculateFeeler(labelInfo, center, radius, isLeftSide);
    }

    // perform 'logical' clipping ourselves
    if (labelInfo.y! < frame.y || labelInfo.y! + labelInfo.height > frame.y + frame.height) {
      slice.label = '';
      slice.hasFeelers = false;
    } else {
      const maxLabelWidth = getMaxLabelWidth(availSpace, labelInfo.x!, isLeftSide);
      const textInfo =
        labelInfo.textInfo &&
        getTextInfo(
          labelInfo.slice.label!,
          { ...labelStyle, ...labelInfo.slice.labelStyle },
          maxLabelWidth,
          labelInfo.textInfo.height!,
          true,
          1,
          getTextDimension
        );
      labelInfo.textInfo = textInfo;
      if (labelInfo.height === 0 || labelInfo.width === 0) {
        slice.label = '';
        slice.hasFeelers = false;
      }
    }
  }
}

export function _calculateFeeler<K>(
  labelInfo: PieLabelInfo<K>,
  center: { cx: number; cy: number },
  radius: number,
  isLeft: boolean
) {
  const { x, y, height } = labelInfo;
  let targetX = x!;
  const targetY = y! + height * _LABEL_TO_FEELER_OFFSET;
  const minHorizLength = _FEELER_HORIZ_MINSIZE * radius;

  let midX;
  if (isLeft) {
    targetX += _LABEL_TO_FEELER_DISTANCE;
    midX = targetX + minHorizLength;
  } else {
    targetX -= _LABEL_TO_FEELER_DISTANCE;
    midX = targetX - minHorizLength;
  }

  const midPt = {
    x: midX,
    y: targetY
  };
  const endPt = {
    x: targetX,
    y: targetY
  };
  const ma = labelInfo.angle;

  const startPt = reflectAngleOverYAxis(ma, center.cx, center.cy, radius, radius);

  // make set the first section of the feeler radial if possible
  const pa = degreesToRads(labelInfo['position']);
  const radFeelerAngle = Math.abs(Math.atan2(midPt.x - startPt.x, startPt.y - midPt.y));
  const horizOffset = (startPt.y - midPt.y) * Math.tan(pa); // * pieChart.getRadiusX() / pieChart.getRadiusY();
  if (
    (pa > Math.PI / 2 && radFeelerAngle > Math.PI / 2 && radFeelerAngle < pa) ||
    (pa < Math.PI / 2 && radFeelerAngle < Math.PI / 2 && radFeelerAngle > pa)
  ) {
    if (isLeft) {
      midPt.x = startPt.x - horizOffset;
    } else {
      midPt.x = startPt.x + horizOffset;
    }
  }

  //store outside feeler points on slice
  //and let slice draw the feeler so that we can
  //easily redraw it when selecting
  labelInfo['startPt'] = startPt;
  labelInfo['midPt'] = midPt;
  labelInfo['endPt'] = endPt;
  return Math.abs(endPt.x - midPt.x) - minHorizLength;
}
